/**
 * SOVRANOVA — About / Full Intro
 * Honest description of what is ACTUALLY live. Wellness companion framing,
 * not medical treatment. No claims of curing, rewiring, or treating conditions.
 */
import { useState } from 'react'

const PILLARS = [
  { emoji: '🤝', title: 'Your Companion', body: 'A voice-first companion that greets you, remembers your past sessions, and talks with you in real time. Tap once and it is hands-free.' },
  { emoji: '🌬️', title: 'Vent', body: 'Unstructured emotional release. Speak freely and be heard — receive, reflect, and a gentle way forward. No fixing, just space.' },
  { emoji: '📓', title: 'Journal', body: 'Reflective writing that stays yours. A quiet place to put down what you are carrying.' },
  { emoji: '🌙', title: 'Hypnosis & Sleep', body: 'Guided relaxation and wind-down audio with paced breathing — used while awake and resting, to settle the nervous system.' },
  { emoji: '✨', title: 'Affirmations', body: 'Personalised affirmations to support how you want to show up.' },
]

const MODULES = [
  { emoji: '🛡️', name: 'MASCULINE', body: "A strength-based companion for men — performance, fatherhood, identity, and the things that are hard to say out loud." },
  { emoji: '🌷', name: 'FEMININE', body: 'A grounded companion for women — motherhood and identity, the mental load, reconnecting with your own voice, and feelings often kept quiet.' },
  { emoji: '🌊', name: 'SOVEREIGN FLOW', body: 'The stories about money, worth, and abundance learned long ago — explored honestly. Never financial advice.' },
]

export function AboutView({ onClose }) {
  const [openFaq, setOpenFaq] = useState(null)
  const faqs = [
    { q: 'Is SOVRANOVA therapy or medical treatment?', a: 'No. SOVRANOVA is a wellness and personal-development companion. It is not therapy, not a medical device, and not a substitute for professional care. If you are struggling, please reach out to a qualified professional — and in crisis, contact Lifeline 13 11 14 (AU), 988 (US), or your local emergency number.' },
    { q: 'Does it really remember me?', a: 'Yes. Your sessions, reflections, and progress are stored securely so the companion can pick up where you left off. Recent context is also kept on your device, so if you lose connection your history is still there and syncs back when you are online again.' },
    { q: 'Can it rewrite my subconscious while I sleep?', a: 'No — and we will never claim that. The science does not support sleep-based "reprogramming." Our sleep and hypnosis audio is for relaxation and wind-down while awake and resting. Anything we explore beyond that would be clearly labelled as research you choose to opt into.' },
    { q: 'Do you measure whether it helps?', a: 'Yes. With your consent, SOVRANOVA uses recognised wellbeing check-ins (PHQ-9 and GAD-7) so you — and we — can see how things are tracking over time. We aim to show what is real, not to make claims we cannot back up.' },
  ]

  return (
    <div style={s.page}>
      <button style={s.back} onClick={onClose}>← Back</button>

      <div style={s.hero}>
        <p style={s.kicker}>About</p>
        <h1 style={s.h1}>◎ SOVRANOVA</h1>
        <p style={s.lede}>A voice-first wellbeing companion that remembers you, listens to how you are really doing, and stays with you over time. Not a content library. Not a chatbot that forgets. A companion built around continuity.</p>
      </div>

      <section>
        <h3 style={s.sectionTitle}>What's here for you</h3>
        <div style={s.list}>
          {PILLARS.map(p => (
            <div key={p.title} style={s.row}>
              <span style={s.rowEmoji}>{p.emoji}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={s.rowTitle}>{p.title}</p>
                <p style={s.rowBody}>{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 style={s.sectionTitle}>Focused journeys</h3>
        <div style={s.list}>
          {MODULES.map(m => (
            <div key={m.name} style={s.row}>
              <span style={s.rowEmoji}>{m.emoji}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={s.rowTitle}>{m.name}</p>
                <p style={s.rowBody}>{m.body}</p>
              </div>
            </div>
          ))}
        </div>
        <p style={s.note}>More journeys are in development and will appear here as they are genuinely ready — never before.</p>
      </section>

      <section>
        <h3 style={s.sectionTitle}>How we measure progress</h3>
        <p style={s.para}>SOVRANOVA tracks recognised wellbeing check-ins (PHQ-9 and GAD-7) with your consent, so progress is based on real measures rather than vague numbers. You can see your own trend on your dashboard.</p>
      </section>

      <section>
        <h3 style={s.sectionTitle}>Honest answers</h3>
        <div style={s.list}>
          {faqs.map((f, i) => (
            <button key={i} style={s.faq} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <p style={s.faqQ}>{openFaq === i ? '▾ ' : '▸ '}{f.q}</p>
              {openFaq === i && <p style={s.faqA}>{f.a}</p>}
            </button>
          ))}
        </div>
      </section>

      <p style={s.foot}>SOVRANOVA is a wellness and personal-development tool, not a medical service. If you are in crisis, contact Lifeline 13 11 14 (AU), 988 (US), or your local emergency number.</p>
    </div>
  )
}

const s = {
  // Organic restyle (2026-08-14): cream reading page — Caprasimo headings,
  // warm ink text, soft n100 cards, terracotta kicker. Values only; the
  // honest copy and FAQ behavior are untouched.
  page:         { minHeight:'100vh', background:'var(--og-bg, #f5ead8)', padding:'1.5rem 1.25rem 2.5rem', display:'flex', flexDirection:'column', gap:'1.5rem', color:'var(--og-text, #201e1d)' },
  back:         { alignSelf:'flex-start', background:'transparent', border:'1.5px solid var(--og-n300, #dcd3c4)', color:'var(--og-n700, #645c50)', fontSize:'0.82rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.9rem', fontFamily:'inherit' },
  hero:         { display:'flex', flexDirection:'column', gap:'0.5rem' },
  kicker:       { fontSize:'0.66rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'var(--og-a700, #8c491a)', fontWeight:700 },
  h1:           { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'2rem', fontWeight:400, color:'var(--og-a900, #402310)', lineHeight:1.15, letterSpacing:'-0.01em' },
  lede:         { fontSize:'0.98rem', color:'var(--og-n800, #474238)', lineHeight:1.65 },
  sectionTitle: { fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.1rem', fontWeight:400, color:'var(--og-a900, #402310)', marginBottom:'0.7rem' },
  list:         { display:'flex', flexDirection:'column', gap:'0.6rem' },
  row:          { display:'flex', alignItems:'flex-start', gap:'0.75rem', background:'var(--og-n100, #f9f4ed)', border:'none', borderRadius:'20px', padding:'0.85rem 0.95rem' },
  rowEmoji:     { fontSize:'1.5rem', flexShrink:0, lineHeight:1.2 },
  rowTitle:     { fontSize:'0.9rem', fontWeight:700, color:'var(--og-n900, #2e2b25)' },
  rowBody:      { fontSize:'0.78rem', color:'var(--og-n700, #645c50)', lineHeight:1.5, marginTop:'0.2rem' },
  para:         { fontSize:'0.88rem', color:'var(--og-n700, #645c50)', lineHeight:1.6 },
  note:         { fontSize:'0.76rem', color:'var(--og-n500, #a19786)', lineHeight:1.5, marginTop:'0.6rem', fontStyle:'italic' },
  faq:          { display:'block', width:'100%', textAlign:'left', background:'var(--og-n100, #f9f4ed)', border:'none', borderRadius:'18px', padding:'0.8rem 0.9rem', cursor:'pointer', fontFamily:'inherit' },
  faqQ:         { fontSize:'0.85rem', fontWeight:600, color:'var(--og-n900, #2e2b25)' },
  faqA:         { fontSize:'0.8rem', color:'var(--og-n700, #645c50)', lineHeight:1.6, marginTop:'0.5rem' },
  foot:         { fontSize:'0.76rem', color:'var(--og-n500, #a19786)', lineHeight:1.6, fontStyle:'italic', borderTop:'1.5px solid var(--og-n200, #eee7db)', paddingTop:'1rem' },
}
